/**
 * Users & roles card container (D-034, D-130, D-131): list (`GET /users`, `/roles`, `/permissions`;
 * edit_user_details), create (`POST /users`) and update (`PATCH /users`) with the manager's own PIN
 * typed inside `UserDialog` (D-018: the body field `pin`), deactivate (`PATCH { active: false }`,
 * D-130) through the PIN gate. The dialog shows the *effective* permission set; this container turns
 * it into `grants` / `revocations` against the selected role's defaults (D-130). The one-time setup
 * link of a new user is shown once inside the dialog (D-037). [C28] The allowed-boxes block lists the office's
 * boxes (`GET /accounts`, cash and digital boxes); the roles editor (`manage_roles`, D-034) creates and edits roles
 * through `RoleDialog`; an inactive user can be reactivated (D-130). Access reset stays on the Security card (D-133).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RoleDialog, UserDialog, UsersCard, type RoleFormValues, type UserFormValues } from "@faylasuf/ui";
import type { PinProof, UpdateUserBody, UserRecord } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";
import { setupLinkFor } from "./dialogs.js";

interface DialogState {
  readonly mode: "create" | "edit";
  readonly userId: string | null;
  readonly values: UserFormValues;
  readonly pin: string;
  readonly error: string | null;
  readonly busy: boolean;
  readonly setupLink: string | null;
}

// [C28] D-034 roles editor
interface RoleDialogState {
  readonly mode: "create" | "edit";
  readonly roleId: string | null;
  readonly builtin: boolean;
  readonly activeUsers: number;
  readonly values: RoleFormValues;
  readonly initial: RoleFormValues;
  readonly pin: string;
  readonly error: string | null;
  readonly busy: boolean;
}

const EMPTY: UserFormValues = { username: "", displayName: "", employeeCode: "", roleId: "", permissions: [], twoFactorRequired: true, workingHours: null, limitIqd: "", limitUsd: "", allowedBoxIds: null };

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

export function UsersContainer() {
  const { source, t } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const gate = usePinGate();
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));

  const canManage = hasPermission("manage_users");
  const canManageRoles = hasPermission("manage_roles");
  const users = useQuery({ queryKey: keys.users, queryFn: () => source.users() });
  const roles = useQuery({ queryKey: keys.roles, queryFn: () => source.roles() });
  const permissions = useQuery({ queryKey: keys.permissions, queryFn: () => source.permissions() });
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [roleDialog, setRoleDialog] = useState<RoleDialogState | null>(null);
  // [C28] D-131 allowed boxes: the office's boxes (a manager without a box-reading token sees an empty list)
  const boxes = useQuery({ queryKey: [...keys.accounts, "user-dialog-boxes"], queryFn: () => source.accounts({ kinds: ["cash_box", "digital_box"] }), enabled: canManage, retry: false });
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: keys.users }), qc.invalidateQueries({ queryKey: keys.roles })]);

  const rolePermissions = (roleId: string): readonly string[] => roles.data?.find((r) => r.id === roleId)?.permissions ?? [];

  const openCreate = () => {
    const defaultRole = roles.data?.find((r) => r.builtin === "cashier") ?? roles.data?.find((r) => r.builtin !== "super_manager");
    setDialog({ mode: "create", userId: null, values: { ...EMPTY, roleId: defaultRole?.id ?? "", permissions: defaultRole?.permissions ?? [] }, pin: "", error: null, busy: false, setupLink: null });
  };
  const openEdit = (u: UserRecord) => {
    setDialog({
      mode: "edit",
      userId: u.id,
      values: {
        username: u.username,
        displayName: u.displayName,
        employeeCode: u.employeeCode ?? "",
        roleId: u.roleId,
        permissions: u.permissions,
        twoFactorRequired: u.twoFactorRequired,
        workingHours: u.workingHours,
        limitIqd: u.limitIqd ?? "",
        limitUsd: u.limitUsd ?? "",
        allowedBoxIds: u.allowedBoxIds,
      },
      pin: "",
      error: null,
      busy: false,
      setupLink: null,
    });
  };

  const patch = (p: Partial<UserFormValues>) => {
    if (!dialog) return;
    // Choosing another role resets the checkboxes to that role's defaults (D-130).
    const next = p.roleId !== undefined && p.roleId !== dialog.values.roleId ? { ...p, permissions: rolePermissions(p.roleId) } : p;
    setDialog({ ...dialog, values: { ...dialog.values, ...next }, error: null });
  };

  const submit = useMutation({
    mutationFn: async (d: DialogState) => {
      const v = d.values;
      if (d.mode === "create") {
        const r = await source.createUser({
          username: v.username.trim().toLowerCase(),
          displayName: v.displayName.trim(),
          ...(v.employeeCode.trim() ? { employeeCode: v.employeeCode.trim() } : {}),
          roleId: v.roleId,
          pin: d.pin,
        });
        return { kind: "created" as const, token: r.setupToken ?? null };
      }
      const cur = users.data?.find((u) => u.id === d.userId);
      if (!cur) throw new Error("USER_UNKNOWN");
      const roleChanged = v.roleId !== cur.roleId;
      const base = rolePermissions(v.roleId);
      const grants = v.permissions.filter((p) => !base.includes(p));
      const revocations = base.filter((p) => !v.permissions.includes(p));
      const body: UpdateUserBody = {
        userId: cur.id,
        pin: d.pin,
        ...(v.username.trim().toLowerCase() !== cur.username ? { username: v.username.trim().toLowerCase() } : {}),
        ...(v.displayName.trim() !== cur.displayName ? { displayName: v.displayName.trim() } : {}),
        ...((v.employeeCode.trim() || null) !== (cur.employeeCode ?? null) ? { employeeCode: v.employeeCode.trim() || null } : {}),
        ...(roleChanged ? { roleId: v.roleId } : {}),
        ...(roleChanged || !sameSet(v.permissions, cur.permissions) ? { grants, revocations } : {}),
        ...(v.twoFactorRequired !== cur.twoFactorRequired ? { twoFactorRequired: v.twoFactorRequired } : {}),
        ...(JSON.stringify(v.workingHours) !== JSON.stringify(cur.workingHours) ? { workingHours: v.workingHours } : {}),
        ...((v.limitIqd || null) !== cur.limitIqd ? { limitIqd: v.limitIqd || null } : {}),
        ...((v.limitUsd || null) !== cur.limitUsd ? { limitUsd: v.limitUsd || null } : {}),
        ...(JSON.stringify(v.allowedBoxIds) !== JSON.stringify(cur.allowedBoxIds) ? { allowedBoxIds: v.allowedBoxIds } : {}),
      };
      await source.updateUser(body);
      return { kind: "updated" as const, token: null };
    },
    onSuccess: async (r, d) => {
      await invalidate();
      if (r.kind === "created") {
        toast(t("toast.user_created"));
        setDialog({ ...d, busy: false, pin: "", setupLink: r.token ? setupLinkFor(r.token) : null });
        if (!r.token) setDialog(null);
      } else {
        toast(t("toast.user_updated"));
        setDialog(null);
      }
    },
    onError: (e, d) => {
      if (isSessionLost(e)) return sessionLost();
      setDialog({ ...d, busy: false, error: errorMessage(t, e) });
    },
  });

  const openRole = (roleId: string | null) => {
    const r = roleId ? roles.data?.find((x) => x.id === roleId) : undefined;
    const values: RoleFormValues = r ? { name: r.name, permissions: r.permissions } : { name: "", permissions: [] };
    setRoleDialog({ mode: r ? "edit" : "create", roleId: r?.id ?? null, builtin: !!r?.builtin, activeUsers: r?.activeUsers ?? 0, values, initial: values, pin: "", error: null, busy: false });
  };
  const submitRole = useMutation({
    mutationFn: async (d: RoleDialogState) => {
      const name = d.values.name.trim();
      if (d.mode === "create") return source.createRole({ name, permissions: d.values.permissions, pin: d.pin });
      return source.updateRole({
        roleId: d.roleId!,
        pin: d.pin,
        ...(name !== d.initial.name ? { name } : {}),
        ...(!sameSet(d.values.permissions, d.initial.permissions) ? { permissions: d.values.permissions } : {}),
      });
    },
    onSuccess: async (_r, d) => {
      await invalidate();
      toast(t(d.mode === "create" ? "toast.role_created" : "toast.role_updated"));
      setRoleDialog(null);
    },
    onError: (e, d) => {
      if (isSessionLost(e)) return sessionLost();
      setRoleDialog({ ...d, busy: false, error: errorMessage(t, e) });
    },
  });

  const reactivate = async (u: UserRecord) => {
    try {
      const r = await gate.withPin((proof: PinProof) => source.updateUser({ userId: u.id, active: true, pin: proof.pin ?? "" }), { message: t("pin.message_reactivate", { user: u.username }) });
      if (r !== undefined) {
        toast(t("toast.user_reactivated"));
        await invalidate();
      }
    } catch (e) {
      fail(e);
    }
  };

  const deactivate = async (u: UserRecord) => {
    try {
      const r = await gate.withPin((proof: PinProof) => source.updateUser({ userId: u.id, active: false, pin: proof.pin ?? "" }), { message: t("pin.message_deactivate", { user: u.username }) });
      if (r !== undefined) {
        toast(t("toast.user_deactivated"));
        await invalidate();
      }
    } catch (e) {
      fail(e);
    }
  };

  const restriction = (u: UserRecord) => (u.allowedBoxIds === null ? t("users.all_boxes") : `${t("users.allowed_boxes")}: ${u.allowedBoxIds.length}`);

  return (
    <>
      <UsersCard
        users={(users.data ?? []).map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName,
          employeeCode: u.employeeCode ?? null,
          roleName: u.roleName,
          active: u.active,
          restriction: restriction(u),
          restrictionIsSpecific: u.allowedBoxIds !== null,
        }))}
        roles={(roles.data ?? []).map((r) => ({ id: r.id, name: r.name, builtin: r.builtin, permissionsCount: r.permissions.length, activeUsers: r.activeUsers }))}
        currentUserId={session.user.id}
        canManage={canManage}
        onEdit={(id) => {
          const u = users.data?.find((x) => x.id === id);
          if (u) openEdit(u);
        }}
        onDeactivate={(id) => {
          const u = users.data?.find((x) => x.id === id);
          if (u) void deactivate(u);
        }}
        onAdd={openCreate}
        {...(canManageRoles ? { onEditRole: (id: string) => openRole(id), onAddRole: () => openRole(null) } : {})}
        {...(canManage ? { onReactivate: (id: string) => { const u = users.data?.find((x) => x.id === id); if (u) void reactivate(u); } } : {})}
      />
      {roleDialog && (
        <RoleDialog
          mode={roleDialog.mode}
          values={roleDialog.values}
          onChange={(p) => setRoleDialog({ ...roleDialog, values: { ...roleDialog.values, ...p }, error: null })}
          permissionCatalogue={permissions.data ?? []}
          activeUsers={roleDialog.activeUsers}
          builtin={roleDialog.builtin}
          pin={roleDialog.pin}
          onPinChange={(pin) => setRoleDialog({ ...roleDialog, pin: pin.replace(/\D/g, "").slice(0, 6), error: null })}
          onSubmit={() => {
            if (!/^\d{6}$/.test(roleDialog.pin)) return setRoleDialog({ ...roleDialog, error: t("security.pin_format") });
            setRoleDialog({ ...roleDialog, busy: true, error: null });
            submitRole.mutate({ ...roleDialog, busy: true });
          }}
          onClose={() => setRoleDialog(null)}
          error={roleDialog.error}
          busy={roleDialog.busy}
        />
      )}
      {gate.dialog}
      {dialog && (
        <UserDialog
          mode={dialog.mode}
          values={dialog.values}
          onChange={patch}
          roles={(roles.data ?? []).map((r) => ({ id: r.id, name: r.name, builtin: r.builtin }))}
          permissionCatalogue={permissions.data ?? []}
          boxes={(boxes.data ?? []).filter((b) => !b.archived).map((b) => ({ id: b.id, name: `${b.name ?? b.kind} (${b.currency})` }))}
          pin={dialog.pin}
          onPinChange={(pin) => setDialog({ ...dialog, pin: pin.replace(/\D/g, "").slice(0, 6), error: null })}
          onSubmit={() => {
            if (dialog.setupLink) return setDialog(null);
            if (!/^\d{6}$/.test(dialog.pin)) return setDialog({ ...dialog, error: t("security.pin_format") });
            setDialog({ ...dialog, busy: true, error: null });
            submit.mutate({ ...dialog, busy: true });
          }}
          onClose={() => setDialog(null)}
          error={dialog.error}
          busy={dialog.busy}
          setupLink={dialog.setupLink}
        />
      )}
    </>
  );
}
